"use client";

import { Button } from "@/components/shadcn/button";
import { Card, CardContent } from "@/components/shadcn/card";
import { Youtube } from "@/icons/youtube";
import { fetcher } from "@/lib/fetcher";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import Link from "next/link";
import YoutubeVideoCard from "./youtube-video-card";

interface YoutubeVideo {
    id: string;
    snippet?: { title?: string; publishedAt?: string };
    statistics?: { viewCount?: number };
}

interface YoutubeChannel {
    statistics?: { subscriberCount?: number };
}

interface YoutubeLatest {
    videos: YoutubeVideo[];
    channel: YoutubeChannel | null;
}

/**
 * Client-side "YouTube'da Yeniler" widget. Fetches the Valkey-cached
 * /api/youtube/latest at runtime so the /haberler page itself can stay fully
 * static (news refreshed via on-demand revalidation) without freezing this
 * list to an empty build-time render.
 */
export default function YoutubeSection() {
    const t = useTranslations("news");
    const { data } = useQuery<YoutubeLatest>({
        queryKey: ["youtube-latest"],
        queryFn: () => fetcher("/api/youtube/latest"),
        staleTime: 60 * 60 * 1000, // server is Valkey-cached daily; 1h client cache
    });

    const youtubeVideos = data?.videos ?? [];

    if (youtubeVideos.length === 0) return null;

    return (
        <Card>
            <CardContent className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">
                        {t("youtubeLatest")}
                    </h2>
                    <Button
                        asChild
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                    >
                        <Link
                            href="https://www.youtube.com/@satranc365"
                            target="_blank"
                        >
                            <Youtube className="size-5" />
                            {t("youtubeGo")}
                        </Link>
                    </Button>
                </div>
                <div className="flex flex-col gap-6 lg:flex-row">
                    <YoutubeVideoCard
                        video={youtubeVideos[0]}
                        className="flex-1"
                    />
                    {youtubeVideos.length > 1 && (
                        <div className="rounded-xl border p-4 lg:max-h-128 lg:w-2/5 lg:overflow-y-auto">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {youtubeVideos.slice(1).map((youtubeVideo) => (
                                    <YoutubeVideoCard
                                        key={youtubeVideo.id}
                                        video={youtubeVideo}
                                        compact
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
